import { BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

// Custom Bar Chart Component using CSS
const CustomBarChart = ({ data }) => {
   const { t } = useTranslation();
  if (!data || data.length === 0) {
    return (
      <div className="h-80 bg-white p-4 rounded-lg border border-white flex flex-col items-center justify-center">
        <h4 className="font-semibold text-center text-slate-700 mb-4">
           {t("performance.chartTitle")}
        </h4>
        <div className="flex-grow flex items-center justify-center text-slate-500">
          <BarChart3 className="w-6 h-6 mr-2" />
          <span>{t("performance.noData")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-80 bg-white p-4 rounded-lg border border-white max-w-lg mx-auto">
      <h4 className="font-semibold mb-4 text-center text-slate-700">
        {t("performance.chartTitle")}
      </h4>
      <ResponsiveContainer width="100%" height="90%">
        <BarChart
          data={data}
          margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="name" fontSize={12} tick={{ fontSize: 12 }} />
          <YAxis fontSize={12} tick={{ fontSize: 12 }} domain={[0, 100]} />
          <Tooltip
            cursor={{ fill: "rgba(238, 242, 255, 0.6)" }}
            contentStyle={{
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "0.5rem",
            }}
          />
          <Legend wrapperStyle={{ fontSize: "14px" }} />
          <Bar dataKey="Definition" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Definition" />
          <Bar dataKey="Explanation" fill="#16a34a" radius={[4, 4, 0, 0]} name="Explanation" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const PerformanceSection = ({ report }) => {
   const { t } = useTranslation();
  const performanceData = report?.performance_ai || {};
  const graphData = Object.entries(performanceData.data_for_graph || {}).map(([topic, values]) => ({
    name: topic,
    ...values,
  }));

  const summaryInsight = performanceData.summary_insight || t("performance.noInsights");
  const inProgressTopics = performanceData.in_progress_topics || [];
  const dataForGraph = performanceData.data_for_graph || {};

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <h3 className="text-lg font-bold mb-4">{t("performance.title")}</h3>
      <div className="space-y-6">
        <div>
          <p className="text-sm text-slate-600 mb-3">{summaryInsight}</p>
        </div>

        {/* Custom Bar Chart */}
        <CustomBarChart data={graphData} />

        <div>
          <h4 className="font-semibold mb-2">{t("performance.topicsInProgress")}</h4>
          {inProgressTopics.length > 0 ? (
            <div className="space-y-2">
              {inProgressTopics.map((topic, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg"
                >
                  <span className="font-medium">{topic}</span>
                  {dataForGraph[topic] && (
                    <div className="flex space-x-2 text-xs">
                      {Object.entries(dataForGraph[topic]).map(([key, value]) => (
                        <span key={key} className="bg-yellow-200 px-2 py-1 rounded">
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t("performance.noTopics")}.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceSection;
